// Reset transient form/UI state when a controlled disclosure (Modal, Popover,
// etc.) closes. The pattern shows up in every modal that holds local state
// across opens — Radix keeps the parent mounted, so `useState(initialX)` only
// initializes once and stale values bleed across opens otherwise.
//
// Usage:
//   useResetOnClose(open, () => {
//     setName(initial.name);
//     setDescription(initial.description);
//     setError(null);
//   }, [initial.name, initial.description]);

import { useEffect, type DependencyList } from 'react';

export function useResetOnClose(
  open: boolean,
  reset: () => void,
  deps: DependencyList = [],
): void {
  useEffect(() => {
    if (open) return;
    reset();
    // We deliberately omit `reset` from deps — callers wrap state setters
    // inline (a fresh closure each render). Instead we depend on `open` plus
    // the caller-provided list (typically the `initial.*` values that should
    // re-seed the form when the parent loads new data while the modal is
    // closed).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ...deps]);
}
